import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Plus, Trash2, Pencil, ShieldCheck, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const ROLES = [
  { value: "super_admin", label: "Super Admin" },
  { value: "company_admin", label: "Company Admin" },
  { value: "recruiter", label: "Recruiter" },
  { value: "data_entry", label: "Data Entry Operator" },
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [forbidden, setForbidden] = useState(false);

  const refresh = async () => {
    try {
      const r = await api.get("/users");
      setUsers(r.data);
    } catch (e) {
      if (e?.response?.status === 403) {
        setForbidden(true);
      } else {
        toast.error("Could not load users");
      }
    }
  };
  useEffect(() => { refresh(); }, []);

  const del = async (uid) => {
    if (!window.confirm("Delete this user? This cannot be undone.")) return;
    try { await api.delete(`/users/${uid}`); toast.success("User deleted"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto">
      {forbidden && (
        <Card className="border-border" data-testid="users-forbidden">
          <CardContent className="p-8 text-center">
            <ShieldCheck className="size-8 mx-auto text-muted-foreground mb-2" />
            <div className="display font-bold text-lg">Admins only</div>
            <p className="text-sm text-muted-foreground mt-1">You don't have permission to manage users. Contact your workspace admin.</p>
          </CardContent>
        </Card>
      )}
      {!forbidden && (<>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground font-semibold">Workspace</div>
          <h1 className="display text-3xl md:text-4xl font-black tracking-tighter mt-1">Users</h1>
          <p className="text-muted-foreground mt-1 text-sm">Manage everyone with access to this workspace.</p>
        </div>
        <Button onClick={() => setCreating(true)} className="lift gap-2" data-testid="users-new-button"><UserPlus className="size-4" /> Invite user</Button>
      </div>

      <Card className="border-border">
        <CardContent className="p-0">
          <div className="divide-y divide-border">
            {users.map((u) => (
              <div key={u.id} className="flex items-center gap-3 p-4" data-testid={`user-row-${u.id}`}>
                <Avatar className="size-10">
                  <AvatarImage src={u.avatar_url ? `${BACKEND_URL}${u.avatar_url}` : undefined} />
                  <AvatarFallback className="bg-primary text-primary-foreground text-sm">{(u.full_name || "?").split(" ").map(s => s[0]).slice(0,2).join("").toUpperCase()}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-bold display truncate">{u.full_name}</div>
                    {u.id === me?.id && <span className="chip">You</span>}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{u.email}{u.company ? ` · ${u.company}` : ""}</div>
                </div>
                <div className="hidden sm:block">
                  <span className="chip flex items-center gap-1"><ShieldCheck className="size-3" /> {u.role.replace("_", " ")}</span>
                </div>
                <Button size="icon" variant="ghost" onClick={() => setEditing(u)} data-testid={`user-edit-${u.id}`}><Pencil className="size-4" /></Button>
                {me?.role === "super_admin" && u.id !== me.id && (
                  <Button size="icon" variant="ghost" onClick={() => del(u.id)} className="text-destructive" data-testid={`user-delete-${u.id}`}><Trash2 className="size-4" /></Button>
                )}
              </div>
            ))}
            {users.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">No users yet.</div>}
          </div>
        </CardContent>
      </Card>

      {creating && <UserDialog onClose={() => { setCreating(false); refresh(); }} mode="create" />}
      {editing && <UserDialog onClose={() => { setEditing(null); refresh(); }} mode="edit" user={editing} />}
      </>)}
    </div>
  );
}

function UserDialog({ onClose, mode, user }) {
  const [form, setForm] = useState({
    email: user?.email || "",
    full_name: user?.full_name || "",
    company: user?.company || "",
    role: user?.role || "recruiter",
    password: "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      if (mode === "create") {
        await api.post("/users", form);
        toast.success("User created");
      } else {
        const payload = { full_name: form.full_name, company: form.company, role: form.role };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${user.id}`, payload);
        toast.success("User updated");
      }
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader><DialogTitle>{mode === "create" ? "Invite a new user" : `Edit ${user.full_name}`}</DialogTitle></DialogHeader>
        <div className="grid gap-3 py-2">
          <Field label="Full name"><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="user-form-name" /></Field>
          {mode === "create" && <Field label="Email"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-form-email" /></Field>}
          <Field label="Company"><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="user-form-company" /></Field>
          <Field label="Role">
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="user-form-role"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label={mode === "create" ? "Initial password" : "New password (leave blank to keep)"}>
            <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-form-password" />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="user-form-submit">{busy ? "Saving…" : mode === "create" ? "Create user" : "Save changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return <div><Label className="text-xs font-semibold">{label}</Label><div className="mt-1.5">{children}</div></div>;
}
